import { useThreadSnoozeClock } from "./hooks/useThreadSnoozeClock";
import {
  createThreadDiscoveryModel,
  type ThreadDiscoveryProject,
  type ThreadDiscoveryModel,
} from "@afar-research/client-runtime/state/thread-discovery";
import { scopeThreadRef } from "@afar-research/client-runtime/environment";
import type { EnvironmentId, ScopedThreadRef } from "@afar-research/contracts";
import { createContext, use, useEffect, useMemo, useRef, type ReactNode } from "react";

import { useClientSettings } from "./hooks/useSettings";
import { useThreadPlacement } from "./hooks/useThreadPlacement";
import { useEnvironments } from "./state/environments";
import { useProjects, useThreadShells } from "./state/entities";
import {
  readThreadDiscoveryCache,
  hydrateCachedThreadDiscoveryShell,
  writeThreadDiscoveryCache,
} from "./threadDiscoveryPersistence";
import type { SidebarThreadSummary } from "./types";
import { selectDiscoveryRows } from "./threadDiscoveryRows.logic";

type CachedEnvironmentRows = {
  readonly projects: ReadonlyArray<ThreadDiscoveryProject>;
  readonly threads: ReturnType<typeof useThreadShells>;
};

function loadCachedRowsByEnvironment(): Map<EnvironmentId, CachedEnvironmentRows> {
  const cache =
    typeof localStorage === "undefined"
      ? { projects: [], threads: [] }
      : readThreadDiscoveryCache(localStorage);
  const environmentIds = new Set([
    ...cache.projects.map((project) => project.environmentId),
    ...cache.threads.map((thread) => thread.environmentId),
  ]);
  return new Map(
    [...environmentIds].map((environmentId) => [
      environmentId,
      {
        projects: cache.projects.filter((project) => project.environmentId === environmentId),
        threads: cache.threads
          .filter((thread) => thread.environmentId === environmentId)
          .map(hydrateCachedThreadDiscoveryShell),
      },
    ]),
  );
}

interface ThreadDiscoveryContextValue {
  readonly model: ThreadDiscoveryModel<SidebarThreadSummary>;
  readonly pinThread: (ref: ScopedThreadRef) => void;
  readonly unpinThread: (ref: ScopedThreadRef) => void;
}

const ThreadDiscoveryContext = createContext<ThreadDiscoveryContextValue | null>(null);
const THREAD_DISCOVERY_CACHE_WRITE_DELAY_MS = 250;

export function ThreadDiscoveryProvider(props: { readonly children: ReactNode }) {
  const projects = useProjects();
  const threads = useThreadShells();
  const sortOrder = useClientSettings((settings) => settings.sidebarThreadSortOrder);
  // Pinning is Environment state, not a client preference: every paired
  // client sees the same pinned block, and a pin survives clearing this
  // browser's storage.
  const pinnedThreadRefs = useMemo(
    () =>
      threads.flatMap((thread) =>
        thread.placement?.pinnedAt != null && thread.archivedAt === null
          ? [scopeThreadRef(thread.environmentId, thread.id)]
          : [],
      ),
    [threads],
  );
  const { pinThread, unpinThread } = useThreadPlacement();
  const { environments } = useEnvironments();
  const cachedRowsByEnvironmentRef = useRef<Map<EnvironmentId, CachedEnvironmentRows> | null>(null);
  const lastPersistedCacheRef = useRef<string | null>(null);
  if (cachedRowsByEnvironmentRef.current === null) {
    cachedRowsByEnvironmentRef.current = loadCachedRowsByEnvironment();
  }
  const readinessByEnvironmentId = useMemo(() => {
    const readiness = new Map<EnvironmentId, boolean>();
    for (const project of projects) readiness.set(project.environmentId, false);
    for (const thread of threads) readiness.set(thread.environmentId, false);
    for (const environment of environments) {
      readiness.set(environment.environmentId, environment.connection.phase === "ready");
    }
    return readiness;
  }, [environments, projects, threads]);
  useEffect(() => {
    const cache = cachedRowsByEnvironmentRef.current;
    if (cache === null) return;
    const configuredEnvironmentIds = new Set(
      environments.map(({ environmentId }) => environmentId),
    );
    for (const environmentId of configuredEnvironmentIds) {
      if (readinessByEnvironmentId.get(environmentId) !== true) continue;
      cache.set(environmentId, {
        projects: projects.filter((project) => project.environmentId === environmentId),
        threads: threads.filter(
          (thread) => thread.environmentId === environmentId && thread.archivedAt === null,
        ),
      });
    }
    for (const environmentId of cache.keys()) {
      if (!configuredEnvironmentIds.has(environmentId)) cache.delete(environmentId);
    }
    if (typeof localStorage === "undefined") return;
    const timeoutId = window.setTimeout(() => {
      try {
        lastPersistedCacheRef.current = writeThreadDiscoveryCache(
          localStorage,
          {
            pinnedThreadRefs,
            projects: [...cache.values()].flatMap((rows) => rows.projects),
            threads: [...cache.values()].flatMap((rows) => rows.threads),
          },
          lastPersistedCacheRef.current,
        );
      } catch {
        // Discovery cache is optional client context; live authority remains unaffected.
      }
    }, THREAD_DISCOVERY_CACHE_WRITE_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [environments, pinnedThreadRefs, projects, readinessByEnvironmentId, threads]);
  const { projects: discoveryProjects, threads: discoveryThreads } = useMemo(
    () =>
      selectDiscoveryRows({
        projects,
        threads,
        cachedRowsByEnvironmentId: cachedRowsByEnvironmentRef.current ?? new Map(),
        readinessByEnvironmentId,
      }),
    [projects, readinessByEnvironmentId, threads],
  );
  const managementNow = useThreadSnoozeClock(discoveryThreads);
  const model = useMemo(
    () =>
      createThreadDiscoveryModel({
        environmentReadinessById: readinessByEnvironmentId,
        pinnedThreadRefs,
        now: managementNow,
        projects: discoveryProjects,
        sortOrder,
        threads: discoveryThreads,
      }),
    [
      discoveryProjects,
      discoveryThreads,
      readinessByEnvironmentId,
      pinnedThreadRefs,
      managementNow,
      sortOrder,
    ],
  );
  const value = useMemo(() => ({ model, pinThread, unpinThread }), [model, pinThread, unpinThread]);

  return <ThreadDiscoveryContext value={value}>{props.children}</ThreadDiscoveryContext>;
}

export function useThreadDiscovery(): ThreadDiscoveryModel<SidebarThreadSummary> {
  const context = use(ThreadDiscoveryContext);
  if (context === null) {
    throw new Error("Thread discovery must be used inside ThreadDiscoveryProvider");
  }
  return context.model;
}

export function useThreadDiscoveryPins(): Pick<
  ThreadDiscoveryContextValue,
  "pinThread" | "unpinThread"
> {
  const context = use(ThreadDiscoveryContext);
  if (context === null) {
    throw new Error("Thread discovery pins must be used inside ThreadDiscoveryProvider");
  }
  return { pinThread: context.pinThread, unpinThread: context.unpinThread };
}
